import { CdkDragDrop } from '@angular/cdk/drag-drop';
import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormArray, FormBuilder, FormControl, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';
import { asapScheduler, scheduled, Subscription } from 'rxjs';
import { catchError, filter, switchMap } from 'rxjs/operators';
import { AddOnDetail } from 'src/app/classes/add-on-detail';
import { Dispatch } from 'src/app/classes/dispatch';
import { DispatchDriverSummary } from 'src/app/classes/dispatch-driver-summary';
import { DispatchPassenger } from 'src/app/classes/dispatch-passenger';
import { DispatchTaskDetails } from 'src/app/classes/dispatch-task-details';
import { DispatchTaskStatus } from 'src/app/classes/dispatch-task-status';
import { DispatchUpdateResponse } from 'src/app/classes/dispatch-update-response';
import { Passenger } from 'src/app/classes/passenger';
import { PassengerAddOnWrapper } from 'src/app/classes/passenger-add-on-wrapper';
import { Profile } from 'src/app/classes/profile';
import { TaskType } from 'src/app/classes/task-type-enum';
import { DispatchService } from 'src/app/services/dispatch.service';
import { ProfileService } from 'src/app/services/profile.service';
import { ToastService } from 'src/app/services/toast.service';
import { UtilitiesService } from 'src/app/services/utilities.service';
import { DispatchConfirmationDialogComponent } from '../dispatch-confirmation-dialog/dispatch-confirmation-dialog.component';

const HH_MM_CTRL_OPTS = { validators: [ Validators.pattern(/^([01]{0,1}[0-9]|2[0-3]):[0-5][0-9]$/) ] };

const TASK_DETAIL_TYPES = [ 'Passenger', 'Paperwork', 'Catering', 'Other' ];

@Component({
  standalone: false,
  selector: 'app-dispatch-item-dropzone',
  templateUrl: './dispatch-item-dropzone.component.html',
  styleUrls: [ './dispatch-item-dropzone.component.scss' ]
})
export class DispatchItemDropzoneComponent implements OnInit, OnChanges, OnDestroy {
  warningIcon = faExclamationTriangle as IconProp;

  @Input() taskDetails: DispatchTaskDetails;
  @Output() updateTask = new EventEmitter<DispatchUpdateResponse>();

  TaskType = TaskType;
  DispatchTaskStatus = DispatchTaskStatus;

  typeDetailTypes: string[] = TASK_DETAIL_TYPES;
  driverList: DispatchDriverSummary[];
  dropListDisabled = false;
  disableDeleteItemButtons = false;

  dispatchDropzoneForm = this.fb.group({
    addPassengersAndAddOnsFormArray: this.fb.array([])
  });

  pickupTimeCtrl: FormControl = this.fb.control('', HH_MM_CTRL_OPTS);
  dropOffTimeCtrl: FormControl = this.fb.control('', HH_MM_CTRL_OPTS);

  loadPickup = false;
  loadDropoff = false;

  subs = new Subscription();

  constructor(
    readonly fb: FormBuilder,
    public dialog: MatDialog,
    readonly dispatchService: DispatchService,
    readonly utilityService: UtilitiesService,
    readonly profileService: ProfileService,
    readonly toastService: ToastService
  ) {}

  ngOnInit(): void {
    this.subs.add(this.subtoDriverSummaryList());
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.taskDetails) {
      const pickupTime = changes.taskDetails.currentValue.actualStartTime;
      const dropOffTime = changes.taskDetails.currentValue.actualEndTime;
      this.pickupTimeCtrl.setValue(pickupTime ? moment(pickupTime).format('HH:mm') : '');
      this.dropOffTimeCtrl.setValue(dropOffTime ? moment(dropOffTime).format('HH:mm') : '');
    }
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  get addPassengersAndAddOnsFormArray(): FormArray {
    return this.dispatchDropzoneForm.get('addPassengersAndAddOnsFormArray') as FormArray;
  }

  get isRemoteParkingTask(): boolean {
    return this.taskDetails && this.taskDetails.taskTypeName === TaskType.RemoteParking;
  }

  get isAdHocTask(): boolean {
    return this.taskDetails && this.taskDetails.taskTypeName === TaskType.Other;
  }

  get isCancelled(): boolean {
    return this.taskDetails && this.taskDetails.status === 'Cancelled';
  }

  get passengers(): Passenger[] {
    return (this.taskDetails && this.taskDetails.metadata && this.taskDetails.metadata.passengers) || [];
  }

  get addOns(): AddOnDetail[] {
    return (this.taskDetails && this.taskDetails.metadata && this.taskDetails.metadata.addOnDetails) || [];
  }

  subtoDriverSummaryList(): Subscription {
    return this.dispatchService
      .getDispatchData()
      .pipe(filter((dispatchData: Dispatch) => !!dispatchData))
      .subscribe((dispatchData: Dispatch) => (this.driverList = dispatchData.driverSummaryList));
  }

  onPassengerOrAddOnDrop(event: CdkDragDrop<DispatchTaskDetails, DispatchTaskDetails, PassengerAddOnWrapper>): void {
    // Guard against no-op drops. A click on a child element of a cdkDrag (e.g. the
    // red X delete button) can be interpreted by Angular CDK as a zero-distance
    // drag and fire cdkDropListDropped with previousContainer === container.
    // Without this guard the server-side "move" call duplicates the row.
    if (event.previousContainer === event.container && event.previousIndex === event.currentIndex) {
      return;
    }
    this.dropListDisabled = true;
    this.dispatchService
      .movePassengerOrAddOnItem(
        event.item.data.item,
        event.previousContainer.data,
        event.container.data,
        event.item.data.itemType
      )
      .pipe(
        catchError((err: any) => {
          console.log(err);
          this.dropListDisabled = false;
          return scheduled([], asapScheduler);
        })
      )
      .subscribe((response: DispatchUpdateResponse) => {
        this.updateTask.emit(response);
        this.dropListDisabled = false;
      });
  }

  removeFromPreviousContainer(
    event: CdkDragDrop<DispatchTaskDetails, DispatchTaskDetails, PassengerAddOnWrapper>
  ): void {
    if (event.previousContainer !== event.container) {
      let prevIndex = -1;

      if (event.item.data.itemType === 'passenger' && event.previousContainer.data.metadata?.passengers) {
        prevIndex = event.previousContainer.data.metadata.passengers.indexOf(event.item.data.item as Passenger);
        event.previousContainer.data.metadata.passengers.splice(prevIndex, 1);
      } else if (event.item.data.itemType === 'addon' && event.previousContainer.data.metadata?.addOnDetails) {
        prevIndex = event.previousContainer.data.metadata.addOnDetails.indexOf(event.item.data.item as AddOnDetail);
        event.previousContainer.data.metadata.addOnDetails.splice(prevIndex, 1);
      }
    }
  }

  wrapPassengerOrAddOn(item: Passenger | AddOnDetail, itemType: 'passenger' | 'addon'): PassengerAddOnWrapper {
    return { item, itemType };
  }

  deletePassengerOrAddOnItem(
    item: Passenger | AddOnDetail,
    task: DispatchTaskDetails,
    type: 'addon' | 'passenger'
  ): void {
    this.disableDeleteItemButtons = true;
    this.dispatchService.deletePassengerOrAddOnItem(item, task, type).subscribe((response: DispatchUpdateResponse) => {
      this.updateTask.emit(response);
      this.disableDeleteItemButtons = false;
    });
  }

  updateTaskTime(taskDetails: DispatchTaskDetails, type: 'pickupTime' | 'dropoffTime'): void {
    const timeCtrl: FormControl = type === 'pickupTime' ? this.pickupTimeCtrl : this.dropOffTimeCtrl;
    if (timeCtrl && timeCtrl.valid) {
      if (type === 'pickupTime') {
        this.loadPickup = true;
      } else {
        this.loadDropoff = true;
      }

      this.dispatchService
        .updateTaskTime(timeCtrl.value ? timeCtrl.value.padStart(5, '0') : '', taskDetails, type)
        .subscribe((response: DispatchUpdateResponse) => {
          this.updateTask.emit(response);
          this.loadPickup = false;
          this.loadDropoff = false;
        });
    }else{
      this.toastService.show('Invalid Time Format! Correct format is \'HH:MM\'', {
        classname: 'bg-danger text-light',
        delay: 4000
      });
    }
  }


  assignDriverFromDropdown(driver: string, task: DispatchTaskDetails): void {
    const driverSummary = this.driverList?.find(d => d.driverId === driver);
    if (driverSummary && !driverSummary.van) {
      this.toastService.show('Selected driver does not have a van assigned.', { classname: 'bg-danger text-light', delay: 8000 });
      return;
    }
    this.dispatchService.assignDriverFromDropdown(driver, task).subscribe((response: DispatchUpdateResponse) => {
      this.updateTask.emit(response);
    });
  }

  getDriverSummaryDisplayName(driver: DispatchDriverSummary): string {
    return this.utilityService.getDriverSummaryDisplayName(driver);
  }

  getStatusColor(status: DispatchTaskStatus): string {
    return this.utilityService.getStatusColor(status);
  }

  hasPassengersOrAddOns(taskDetails: DispatchTaskDetails): boolean {
    if (taskDetails && taskDetails.metadata) {
      return !!((taskDetails.metadata.passengers || []).length || (taskDetails.metadata.addOnDetails || []).length);
    }
    return false;
  }

  pushNewPassengerOrAddOnToForm(e: MouseEvent): void {
    e.stopImmediatePropagation();
    this.addPassengersAndAddOnsFormArray.push(
      this.fb.group({ type: this.fb.control('', Validators.required), name: this.fb.control('', Validators.required) })
    );
  }

  removeNewPassengerOrAddOnFormItem(formItemIndex: number): void {
    this.dialog.open(DispatchConfirmationDialogComponent,{
      width: '550px',
      data: {
        message: 'Are you sure you want to delete this item?'
      }
    }).afterClosed().subscribe(response => {
      if (response) {
        this.addPassengersAndAddOnsFormArray.removeAt(formItemIndex);
      }
    });
  }

  addPassengerOrAddOn(formItemIndex: number, flightId: number): void {
    if (!this.addPassengersAndAddOnsFormArray.at(formItemIndex).valid) {
      this.toastService.show('Please make sure the add-on has a name and type', {
        classname: 'bg-danger text-light',
        delay: 4000
      });
    } else {
      this.profileService
        .getProfileObs()
        .pipe(
          switchMap((profile: Profile) => {
            const taskDetails: DispatchPassenger = {
              taskName: this.taskDetails.taskName,
              type: 'createAddOn',
              updatedBy: profile.adId,
              newAddOn: {
                ...this.addPassengersAndAddOnsFormArray.at(formItemIndex).value,
                id: this.utilityService.guid()
              }
            };

            return this.dispatchService.addPassengerOrAddOnItem(flightId, taskDetails);
          })
        )
        .subscribe((response: DispatchUpdateResponse) => {
          this.addPassengersAndAddOnsFormArray.removeAt(formItemIndex);
          this.updateTask.emit(response);
        });
    }
  }
}
